import { normalizePerson } from "./people.js";

export const AMAZON_CHARGE_WINDOW_DAYS = 5;

const MONTHS = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

export function parseCsv(text) {
  const rows = [];
  let row = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(current);
      current = "";
    } else if (char === "\n") {
      row.push(current);
      rows.push(row);
      row = [];
      current = "";
    } else if (char !== "\r") {
      current += char;
    }
  }

  if (current.length > 0 || row.length > 0) {
    row.push(current);
    rows.push(row);
  }

  return rows.filter((cells) => cells.some((cell) => String(cell).trim() !== ""));
}

export function parseOrderDate(value) {
  const match = String(value || "")
    .trim()
    .match(/^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/);
  if (!match) return null;
  const month = MONTHS[match[1].toLowerCase()];
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (!month || day < 1 || day > 31) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function parseMoney(value) {
  if (value == null || String(value).trim() === "") return null;
  const match = String(value).replace(/,/g, "").match(/-?\$?\s*(\d+(?:\.\d{1,2})?)/);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount)) return null;
  return Math.round(amount * 100) / 100;
}

export function isAmazonDescription(description) {
  return /amazon|amzn/i.test(description || "");
}

function isCancelled(status) {
  return /cancel/i.test(status || "");
}

function daysAfter(orderDate, chargeDate) {
  const [oy, om, od] = orderDate.split("-").map(Number);
  const [cy, cm, cd] = chargeDate.split("-").map(Number);
  const orderUtc = Date.UTC(oy, om - 1, od);
  const chargeUtc = Date.UTC(cy, cm - 1, cd);
  return Math.round((chargeUtc - orderUtc) / 86400000);
}

function shortTitle(title) {
  const text = String(title || "").replace(/\s+/g, " ").trim();
  if (text.length <= 90) return text;
  return `${text.slice(0, 87)}...`;
}

export function parseAmazonOrderCsv(text) {
  const rows = parseCsv(text);
  if (rows.length === 0) {
    throw new Error("The spreadsheet is empty");
  }

  const headers = rows[0].map((header) => header.trim());
  const index = Object.fromEntries(headers.map((header, i) => [header, i]));
  for (const required of ["orderId", "orderPlaced", "total", "shipmentStatus"]) {
    if (index[required] == null) {
      throw new Error("This file is not an Amazon order history spreadsheet");
    }
  }

  const orders = [];
  const skipped = [];

  for (const cells of rows.slice(1)) {
    const orderId = (cells[index.orderId] || "").trim();
    if (!orderId || orderId.toUpperCase() === "TOTALS") continue;

    const orderPlaced = parseOrderDate(cells[index.orderPlaced]);
    const total = parseMoney(cells[index.total]);
    const shipmentStatus = (cells[index.shipmentStatus] || "").trim();
    const itemTitle = shortTitle(cells[index.itemTitles]);
    const base = { orderId, orderPlaced, total, shipmentStatus, itemTitle };

    if (!orderPlaced) {
      skipped.push({ ...base, reason: "The order date could not be read" });
      continue;
    }
    if (isCancelled(shipmentStatus)) {
      skipped.push({ ...base, reason: "Cancelled" });
      continue;
    }
    if (total == null || total <= 0) {
      skipped.push({ ...base, reason: "No card charge" });
      continue;
    }

    orders.push(base);
  }

  return { orders, skipped };
}

function orderSummary(order) {
  return {
    orderId: order.orderId,
    orderPlaced: order.orderPlaced,
    total: order.total,
    itemTitle: order.itemTitle,
    shipmentStatus: order.shipmentStatus,
  };
}

function chargeSummary(transaction, dayDiff) {
  return {
    transactionId: transaction.id,
    chargeDate: transaction.date,
    chargeAmount: transaction.amount,
    description: transaction.description,
    currentPerson: normalizePerson(transaction.responsible_person),
    dayDiff,
  };
}

/**
 * Pair Amazon orders to card charges.
 * A charge matches when the description is Amazon, the amount matches to the cent,
 * and the charge date is the order date through five days later.
 * Each order and each charge is used once. The closest date wins.
 * Charges already assigned to someone else are left alone and reported as conflicts.
 */
export function matchAmazonOrders(orders, transactions, person) {
  const targetPerson = normalizePerson(person);
  if (!targetPerson) {
    throw new Error("Choose a person for this spreadsheet");
  }

  const charges = transactions
    .map((transaction) => ({
      ...transaction,
      amount: Number(transaction.amount),
      date: String(transaction.date || "").slice(0, 10),
    }))
    .filter(
      (transaction) =>
        transaction.id &&
        transaction.date &&
        transaction.amount > 0 &&
        isAmazonDescription(transaction.description),
    );

  const pairs = [];
  for (const order of orders) {
    for (const transaction of charges) {
      if (Math.abs(transaction.amount - order.total) >= 0.005) continue;
      const dayDiff = daysAfter(order.orderPlaced, transaction.date);
      if (dayDiff < 0 || dayDiff > AMAZON_CHARGE_WINDOW_DAYS) continue;
      pairs.push({ order, transaction, dayDiff });
    }
  }

  pairs.sort(
    (a, b) =>
      a.dayDiff - b.dayDiff ||
      a.order.orderPlaced.localeCompare(b.order.orderPlaced) ||
      a.order.orderId.localeCompare(b.order.orderId),
  );

  const usedOrders = new Set();
  const usedCharges = new Set();
  const matched = [];

  for (const pair of pairs) {
    if (usedOrders.has(pair.order.orderId) || usedCharges.has(pair.transaction.id)) {
      continue;
    }
    const currentPerson = normalizePerson(pair.transaction.responsible_person);
    if (currentPerson && currentPerson !== targetPerson) {
      continue;
    }
    usedOrders.add(pair.order.orderId);
    usedCharges.add(pair.transaction.id);
    matched.push({
      ...orderSummary(pair.order),
      ...chargeSummary(pair.transaction, pair.dayDiff),
      action: currentPerson === targetPerson ? "already" : "update",
    });
  }

  const conflicts = [];
  const unmatched = [];

  for (const order of orders) {
    if (usedOrders.has(order.orderId)) continue;
    const conflictPair = pairs
      .filter(
        (pair) =>
          pair.order.orderId === order.orderId &&
          !usedCharges.has(pair.transaction.id) &&
          normalizePerson(pair.transaction.responsible_person) &&
          normalizePerson(pair.transaction.responsible_person) !== targetPerson,
      )
      .sort((a, b) => a.dayDiff - b.dayDiff)[0];

    if (conflictPair) {
      conflicts.push({
        ...orderSummary(order),
        ...chargeSummary(conflictPair.transaction, conflictPair.dayDiff),
      });
    } else {
      unmatched.push(orderSummary(order));
    }
  }

  return {
    person: targetPerson,
    windowDays: AMAZON_CHARGE_WINDOW_DAYS,
    matched: matched.filter((row) => row.action === "update"),
    already: matched.filter((row) => row.action === "already"),
    conflicts,
    unmatched,
  };
}
